
import { AlertCircle, Check, Loader2, RefreshCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatCurrency } from "@/lib/utils";
import { PaymentDetails, CustomerPermit, SiteBooking } from "@/types/payment";

interface PermitDetailsSectionProps {
  permitDetails: CustomerPermit[];
  bookingDetails: SiteBooking[]; // We'll keep this for backward compatibility but not use it
  loading: boolean;
  showRefreshButton: boolean;
  handleRefresh: () => void;
  retryCount: number;
  maxRetries: number;
  paymentDetails: PaymentDetails | null;
  permitEmails?: Array<{id: string, email: boolean}>;
  resendingEmail: string | null;
  onResendEmail?: (permitId: string) => Promise<void>;
}

export const PermitDetailsSection = ({ 
  permitDetails, 
  loading, 
  showRefreshButton,
  handleRefresh,
  retryCount,
  maxRetries,
  paymentDetails,
  permitEmails,
  resendingEmail,
  onResendEmail
}: PermitDetailsSectionProps) => {
  const isGeneralCamping = (type?: string) => type === 'general-camping';

  const formatPermitType = (type: string) => {
    if (!type) return "Unknown";
    if (isGeneralCamping(type)) return "General Camping";

    return type.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('Two Poles', '- Two Poles');
  };

  const getPermitDates = (detail: CustomerPermit) => {
    if (isGeneralCamping(detail.permit_type)) {
      const s = detail.camping_start_date ? new Date(detail.camping_start_date).toLocaleDateString() : '';
      const e = detail.camping_end_date ? new Date(detail.camping_end_date).toLocaleDateString() : '';
      return `${s} to ${e}`;
    }
    const startDate = new Date(detail.fishing_start_date).toLocaleDateString();
    const endDate = new Date(detail.fishing_end_date).toLocaleDateString();
    return `${startDate} to ${endDate}`;
  };


  // Email status mapping
  const getEmailStatusForItem = (itemId: string) => {
    const match = permitEmails?.find(p => p.id === itemId);
    return match?.email || false;
  };

  if (loading) {
    return (
      <div className="w-full max-w-2xl mb-6">
        <div className="flex items-center justify-center space-x-2 p-6 text-tribal-neutral">
          <Loader2 className="h-5 w-5 animate-spin" />
          <p>Loading your purchase details...</p>
        </div>
      </div>
    );
  }

  const hasFishingPermits = permitDetails.filter(p => p.itemType === 'fishing').length > 0;
  
  if (!hasFishingPermits) {
    // Check if permits are expected but not loaded yet
    if (paymentDetails?.createdPermitIds?.length && paymentDetails.createdPermitIds.length > 0) {
      return (
        <div className="w-full max-w-2xl mb-6">
          <Alert variant="default" className="mb-4">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertTitle>Fishing Permit Information</AlertTitle>
            <AlertDescription>
              We're still processing your fishing permit information.
            </AlertDescription>
          </Alert>

          {showRefreshButton && (
            <Button
              variant="outline"
              onClick={handleRefresh}
              className="flex items-center gap-2 mx-auto"
            >
              <RefreshCw size={16} className={retryCount > 0 ? "animate-spin" : ""} />
              Refresh {retryCount > 0 ? `(Attempt ${retryCount}/${maxRetries})` : ""}
            </Button>
          )}
        </div>
      );
    }
    
    // No fishing permits expected or loaded
    return null;
  }

  const visiblePermits = permitDetails.filter(detail => detail.itemType === 'fishing');
  const allGeneralCamping = visiblePermits.length > 0 && visiblePermits.every(p => isGeneralCamping(p.permit_type));
  const anyGeneralCamping = visiblePermits.some(p => isGeneralCamping(p.permit_type));
  const headingNoun = allGeneralCamping ? 'Camping Permit' : anyGeneralCamping ? 'Permit' : 'Fishing Permit';

  return (
    <div className="w-full max-w-2xl mb-6">
      <h3 className="font-semibold text-lg mb-3">
        {visiblePermits.length > 1 ? `Your ${headingNoun}s` : `Your ${headingNoun}`}
      </h3>
      
      {visiblePermits.map((detail) => {
        const gc = isGeneralCamping(detail.permit_type);
        const emailStatus = getEmailStatusForItem(detail.permit_id);
        
        return (
          <Card key={detail.permit_id} className="p-4 mb-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-base">{formatPermitType(detail.permit_type)}</h4>
                  <Badge variant="outline" className="text-xs">
                    {detail.permit_id}
                  </Badge>
                </div>
                <p className="text-sm text-tribal-neutral mt-1">{getPermitDates(detail)}</p>
              </div>
               <div className="text-right">
                 <p className="text-sm text-tribal-neutral">Subtotal: {formatCurrency(detail.subtotal || detail.permit_price)}</p>
                 {detail.processing_fee > 0 && (
                   <p className="text-sm text-tribal-neutral">Processing Fee: {formatCurrency(detail.processing_fee)}</p>
                 )}
                 <p className="font-semibold">Total: {formatCurrency(detail.total_price)}</p>
                 <p className="text-xs text-tribal-neutral mt-1">
                   Status: <span className="text-green-600 font-medium">Paid</span>
                 </p>
               </div>
            </div>
            
            <hr className="my-3" />
            
            <div className="flex justify-between items-center text-sm">
              <div>
                <p>
                  <span className="font-medium">Type:</span> {formatPermitType(detail.permit_type)}
                  {!gc && detail.extra_poles > 0 && " (with extra poles)"}
                </p>
                {detail.customer_name && (
                  <p><span className="font-medium">Customer:</span> {detail.customer_name}</p>
                )}
              </div>
              

              
              {onResendEmail && (
                <div className="text-right">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="flex items-center gap-1 text-xs" 
                    disabled={resendingEmail === detail.permit_id}
                    onClick={() => onResendEmail(detail.permit_id)}
                  >
                    {resendingEmail === detail.permit_id ? (
                      <><Loader2 size={14} className="animate-spin" /> Sending...</>
                    ) : (
                      <>
                        {emailStatus ? (
                          <><Check size={14} className="text-green-600" /> Email Sent</>
                        ) : (
                          <><Send size={14} /> Send Email</>
                        )}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        );
      })}
      
      {showRefreshButton && (
        <Button
          variant="outline"
          onClick={handleRefresh}
          className="flex items-center gap-2 mx-auto mt-2"
        >
          <RefreshCw size={16} />
          Refresh to show all items {retryCount > 0 ? `(${retryCount}/${maxRetries})` : ""}
        </Button>
      )}
    </div>
  );
};
